import React, { useState } from 'react';
import { Meeting, OutcomesConflictError } from '../../../../types/meeting';
import meetingsAPI from '../../../../services/meetingsApi';

interface MeetingOutcomesActionsProps {
  meeting: Meeting;
  canApprove: boolean;
  approved: boolean;
  onApproved: (meeting: Meeting) => void;
  onError: (message: string) => void;
  // Refetches the meeting so the approver can read the current text before
  // signing off again after a conflict.
  onReload?: () => Promise<Meeting>;
}

// Outcomes actions (FE spec ALSO): Approve (secretary-only) + Export PDF/PPTX.
// Endpoints ship on a sibling branch (PR 4152); a 404 degrades gracefully
// (buttons show a note) and a 409 means the minutes are not approved yet.
const MeetingOutcomesActions: React.FC<MeetingOutcomesActionsProps> = ({
  meeting,
  canApprove,
  approved,
  onApproved,
  onError,
  onReload,
}) => {
  const [approving, setApproving] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'pptx' | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    setStatus(null);
    setConflict(null);
    try {
      const updated = await meetingsAPI.approveOutcomes(meeting.meeting_id, meeting._etag);
      onApproved(updated);
      setStatus('Outcomes approved.');
    } catch (err) {
      // A conflict means the text moved after it was read: approving anyway
      // would sign off on something the approver never saw, so ask them to
      // review the current version instead of retrying behind their back.
      if (err instanceof OutcomesConflictError) {
        setConflict(err.message);
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to approve outcomes';
      if (message.includes('404')) {
        setStatus('Approval is not available yet.');
      } else {
        onError(message);
      }
    } finally {
      setApproving(false);
    }
  };

  // Pulls the current version into the workspace so the approver reads what
  // they are signing off on; the refreshed token makes a re-approve succeed.
  const handleReload = async () => {
    if (!onReload) return;
    setReloading(true);
    try {
      await onReload();
      setConflict(null);
      setStatus('Loaded the current outcomes. Review them, then approve.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to reload outcomes');
    } finally {
      setReloading(false);
    }
  };

  const triggerDownload = async (response: Response, format: 'pdf' | 'pptx') => {
    const contentType = response.headers.get('content-type') || '';
    let blob: Blob;
    let filename = `${meeting.title || 'minutes'}.${format}`;
    if (contentType.includes('application/json')) {
      // Export endpoints may return { success, pdf_base64|pptx_base64, filename }.
      const data = await response.json();
      const b64 = data.pdf_base64 || data.pptx_base64;
      if (!b64) throw new Error('Export returned no document');
      if (data.filename) filename = data.filename;
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      blob = new Blob([bytes], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
    } else {
      blob = await response.blob();
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleExport = async (format: 'pdf' | 'pptx') => {
    setExporting(format);
    setStatus(null);
    try {
      const response = await meetingsAPI.exportOutcomes(meeting.meeting_id, format);
      if (response.status === 404) {
        setStatus('Export is not available yet.');
        return;
      }
      if (response.status === 409) {
        setStatus('Approve the outcomes before exporting.');
        return;
      }
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }
      await triggerDownload(response, format);
      setStatus(`Exported ${format.toUpperCase()}.`);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="meeting-panel">
      <div className="meeting-panel-header">
        <div>
          <h3 className="meeting-panel-title">Finalize outcomes</h3>
          <p className="meeting-panel-subtitle">Approve the minutes, then export to share.</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '4px 0' }}>
        {canApprove && (
          <button
            className="meeting-btn meeting-btn-primary meeting-btn-sm"
            onClick={handleApprove}
            disabled={approving || approved}
          >
            {approved ? '✓ Approved' : approving ? 'Approving…' : 'Approve'}
          </button>
        )}
        <button
          className="meeting-btn meeting-btn-secondary meeting-btn-sm"
          onClick={() => handleExport('pdf')}
          disabled={exporting !== null}
        >
          {exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}
        </button>
        <button
          className="meeting-btn meeting-btn-secondary meeting-btn-sm"
          onClick={() => handleExport('pptx')}
          disabled={exporting !== null}
        >
          {exporting === 'pptx' ? 'Exporting…' : 'Export PPTX'}
        </button>
      </div>
      {conflict && (
        <div className="outcomes-editor-notice outcomes-editor-notice-conflict" role="alert">
          <span className="outcomes-editor-notice-icon" aria-hidden="true">⚠</span>
          <span>
            {conflict} <strong>Nothing was approved.</strong>
          </span>
          {onReload && (
            <button
              type="button"
              className="meeting-btn meeting-btn-secondary meeting-btn-sm"
              onClick={handleReload}
              disabled={reloading}
            >
              {reloading ? 'Reloading…' : 'Load current version'}
            </button>
          )}
        </div>
      )}
      {status && <div className="profile-hint" style={{ marginTop: 6 }}>{status}</div>}
    </div>
  );
};

export default MeetingOutcomesActions;
